import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'
import type { CartItem, StorefrontProduct } from '../../shared/types'

interface CartContextValue {
  items: CartItem[]
  itemCount: number
  subtotal: number
  add: (product: StorefrontProduct, quantity?: number, selections?: Record<string, string>) => void
  setQuantity: (key: string, quantity: number) => void
  remove: (key: string) => void
  clear: () => void
}

const CartContext = createContext<CartContextValue | null>(null)

/**
 * Composite line key: productId plus its chosen options (sorted by group name so key order is
 * stable), so the same product with different preferences is distinct cart lines (D-073). An
 * optionless product has no selections → the key is just its id.
 */
function cartKey(productId: string, selections?: Record<string, string>): string {
  if (!selections || Object.keys(selections).length === 0) {
    return productId
  }
  const opts = Object.keys(selections)
    .sort()
    .map((g) => `${g}=${selections[g]}`)
    .join(';')
  return `${productId}|${opts}`
}

// sessionStorage, not just memory: checkout needs a login, and a cheap Android phone often discards
// the tab while the customer is in the SMS app reading the code. sessionStorage survives that
// reload but not a new tab, so an old cart never resurfaces days later.
const STORAGE_KEY = 'dukaano.cart'

function loadCart(): CartItem[] {
  try {
    return JSON.parse(sessionStorage.getItem(STORAGE_KEY) ?? '[]') as CartItem[]
  } catch {
    return [] // storage blocked or junk in it: start empty
  }
}

/** Client-side cart. Quantities/subtotal are indicative; the server re-prices on checkout. */
export function CartProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<CartItem[]>(loadCart)

  useEffect(() => {
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(items))
    } catch {
      /* storage blocked (private mode, quota): the cart still works for this page */
    }
  }, [items])

  const value = useMemo<CartContextValue>(() => {
    const add = (product: StorefrontProduct, quantity = 1, selections?: Record<string, string>) =>
      setItems((prev) => {
        const key = cartKey(product.id, selections)
        const existing = prev.find((i) => i.key === key)
        if (existing) {
          return prev.map((i) => (i.key === key ? { ...i, quantity: i.quantity + quantity } : i))
        }
        return [...prev, { product, quantity, selections, key }]
      })

    const setQuantity = (key: string, quantity: number) =>
      setItems((prev) =>
        quantity <= 0
          ? prev.filter((i) => i.key !== key)
          : prev.map((i) => (i.key === key ? { ...i, quantity } : i)))

    const remove = (key: string) => setItems((prev) => prev.filter((i) => i.key !== key))
    const clear = () => setItems([])

    const itemCount = items.reduce((n, i) => n + i.quantity, 0)
    // Priceless shops (D-069) carry null prices → treat as 0; the UI hides the subtotal entirely
    // when the shop hides prices, so this value is never shown in that case.
    const subtotal = items.reduce((sum, i) => sum + (i.product.price ?? 0) * i.quantity, 0)

    return { items, itemCount, subtotal, add, setQuantity, remove, clear }
  }, [items])

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>
}

export function useCart(): CartContextValue {
  const ctx = useContext(CartContext)
  if (!ctx) {
    throw new Error('useCart must be used within a CartProvider')
  }
  return ctx
}
